import { ParsedQuery } from 'query-string';
import { StatsigClient } from '@statsig/react-bindings';
import API_URLS from '../../constants/api-urls';
import axios from '../../utils/axios';
import cookiesManager from '../../utils/browser-storage/cookies';
import { storageKeys } from '../../utils/browser-storage/storage-keys';
import detectBrowser from '../../utils/detect-browser';
import detectDevice from '../../utils/detect-device';
import { CatalogProps } from '../../utils/enhanced-ecommerce';
import isPrerender from '../../utils/is-prerender';
import isUITest from '../../utils/is-ui-test';
import isNewMaterial from '../../utils/material/is-new-material';
import { getPosthogFlagsCalled } from '../../utils/posthog-helpers';
import formatPrice from '../../utils/price/format-price';
import session from '../../utils/session';
import serializeQueryParams from '../../utils/url/serialize-query-params';

type Payload = {
    discount: number;
    extra: string | null;
    ga_client_id: string;
    ga_load_status: string;
    ga_track_id: string;
    internal_path: string;
    item_id: string | number | null;
    item_labels: string[];
    item_price: number;
    page_url: string;
    position: number;
    purchase_id: number;
    query: string;
    query_params: string;
    referrer: string;
    source: string;
    test_segment: string;
    total_results: number;
    track_sid: string;
    type: string;
    user_agent: string | null;
    user_device: string;
    user_id: string;
    utm_campaign: string;
    utm_content: string;
    utm_medium: string;
    utm_source: string;
    utm_term: string;
};

export type QualityEventData = Partial<Payload>;

const QEservice = {
    post: (data: { events: QualityEventData[] }) => axios.post(API_URLS.QUALITY_EVENTS, data)
};

const isEmpty = (obj: ParsedQuery) => !Object.keys(obj).length;

const setLabels = (item: Material) => {
    const itemLabels = [];

    if (item.bestInCategories && item.bestInCategories.length) {
        itemLabels.push('Bestseller');
    }

    if (isNewMaterial(item)) {
        itemLabels.push('New');
    }

    if (item.sale) {
        itemLabels.push('Sale');
    }

    if (itemLabels.length) {
        return itemLabels;
    }
};

const priceInCents = (p: string | number) => {
    let price = p;

    price = formatPrice(price);
    price = price.replace(',', '.');
    price = parseFloat(price);
    price *= 100;

    return Math.floor(price);
};

// eslint-disable-next-line
const filterParams = (obj: ParsedQuery) => {
    const searchObj = { ...obj };

    delete searchObj.utm_source;
    delete searchObj.utm_medium;
    delete searchObj.utm_term;
    delete searchObj.utm_content;
    delete searchObj.utm_campaign;
    delete searchObj.internal_path;
    delete searchObj.query;

    if (!isEmpty(searchObj)) {
        return searchObj;
    }
};

const getAbTestsData = () => {
    const ab_tests = {
        testSegment: window.storage.getItem(storageKeys.abtests.testSegment),
        testSegment0: window.storage.getItem(storageKeys.abtests.testSegment0),
        testSegment1: window.storage.getItem(storageKeys.abtests.testSegment1),
        testSegment10: window.storage.getItem(storageKeys.abtests.testSegment10),
        testSegment11: window.storage.getItem(storageKeys.abtests.testSegment11),
        testSegment12: window.storage.getItem(storageKeys.abtests.testSegment12),
        testSegment13: window.storage.getItem(storageKeys.abtests.testSegment13),
        testSegment14: window.storage.getItem(storageKeys.abtests.testSegment14),
        testSegment2: window.storage.getItem(storageKeys.abtests.testSegment2),
        testSegment3: window.storage.getItem(storageKeys.abtests.testSegment3),
        testSegment4: window.storage.getItem(storageKeys.abtests.testSegment4),
        testSegment5: window.storage.getItem(storageKeys.abtests.testSegment5),
        testSegment6: window.storage.getItem(storageKeys.abtests.testSegment6),
        testSegment7: window.storage.getItem(storageKeys.abtests.testSegment7),
        testSegment8: window.storage.getItem(storageKeys.abtests.testSegment8),
        testSegment9: window.storage.getItem(storageKeys.abtests.testSegment9)
    };

    let res = {
        ...(getAbTestStatsigData())
    };

    Object.entries(ab_tests).forEach((entry) => {
        const [, value] = entry;

        if (value?.indexOf('{') > -1) {
            try {
                const posthogFlags = getPosthogFlagsCalled();

                res = { ...res, ...JSON.parse(value), ...posthogFlags };
            } catch (e) {
                console.log(e);
            }
        }
    });

    return res;
};

const getAbTestStatsigData = () => {
    const statsigValues = StatsigClient.instance().getContext().values;
    const statsigABTests = {};

    if (statsigValues?.dynamic_configs) {
        Object.values(statsigValues.dynamic_configs).forEach((testData) => {
            const testName = Object.keys(testData.value)?.[0];
            const testValue = Object.values(testData.value)?.[0];

            statsigABTests[testName] = testValue;
        });
    }

    return statsigABTests;
};

const isExactlyUiTest = () => {
    return (
        isUITest() ||
        window.storage.getItem(storageKeys.general.uiTest) !== null ||
        window.location.search.indexOf('uiTest') > -1
    );
};

const envMixin = () => {
    const urlParams = serializeQueryParams();
    const statsigStableId = StatsigClient.instance().getContext().stableID;

    return {
        ab_tests: isExactlyUiTest() ? {} : getAbTestsData(),
        ga_client_id: cookiesManager.getItem(storageKeys.general.GAClientId),
        ga_track_id: cookiesManager.getItem(storageKeys.general.GATrackId),
        page_url: window.location.href,
        query: ((urlParams.query as string) || '').replace(/%sl%/g, '/'),
        query_params: filterParams(urlParams),
        referrer: document.referrer,
        track_sid: window.storage.getItem(storageKeys.general.trackSid) || null,
        user_agent: detectBrowser(),
        user_device: detectDevice(),
        user_id: window.parseInt(window.storage.getItem(storageKeys.user.userId)) || null,
        utm_campaign: urlParams.utm_campaign,
        utm_content: urlParams.utm_content,
        utm_medium: urlParams.utm_medium,
        utm_source: urlParams.utm_source,
        utm_term: urlParams.utm_term,
        ...(statsigStableId && { user_region: `${statsigStableId}`, stable_id: `${statsigStableId}` }), // TODO: As we don't have stableId field in the QE table, we use user_region as the field is always empty
    };
};

const qualityEventsHelpers = {
    extractMaterialsData(data: Material | Material[], params = {} as CatalogProps) {
        const extract = (item: Material, index = 0) => ({
            item_id: item.id,
            item_labels: setLabels(item),
            item_price:
                `${item.price}` === 'null' || `${item.price}` === 'undefined' ? undefined : priceInCents(item.price),
            position: typeof params.itemIndex === 'undefined' ? index + 1 : params.itemIndex + 1
        });

        if (Array.isArray(data)) {
            return data.map(extract);
        }

        return extract(data);
    },

    extractPurchasedMaterialsData(data: Material[]) {
        return data.map((item) => ({
            discount: item.discount,
            item_id: item.id,
            item_labels: setLabels(item),
            item_price: item.finalPrice,
            purchase_id: item.purchase_id
        }));
    },

    handleQEtrackError(results: QualityEventData[]) {
        if (!window.failedQE) {
            window.failedQE = [];
        }

        window.failedQE.push(results);
    },

    handleQEtrackSuccess(results: QualityEventData[]) {
        if (!window.QE) {
            window.QE = [];
        }

        window.QE.push(results);
    },

    postpone(obj: QualityEventData) {
        window.storage.setItem(JSON.stringify(obj));
    },

    send(
        payload: QualityEventData[],
        resolve: (value: void | PromiseLike<void>) => void,
        reject: (reason?: string) => void
    ) {
        if (session.impersonated()) {
            resolve();

            return;
        }

        QEservice.post({ events: payload })
            .then((res = {}) => {
                if (res.track_sid) {
                    window.storage.setItem(storageKeys.general.trackSid, res.track_sid);
                }

                this.handleQEtrackSuccess(payload);
                resolve();
            })
            .catch(() => {
                this.handleQEtrackError(payload);
                reject();
            });
    },

    track(data: QualityEventData | QualityEventData[]): Promise<void> {
        if (isPrerender()) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            const payload = (
                Array.isArray(data) ? data.map((item) => ({ ...item, ...envMixin() })) : [{ ...envMixin(), ...data }]
            ) as QualityEventData[];

            this.send(payload, resolve, reject);
        });
    },

    trackMaterialList(arr: QualityEventData[], additional: QualityEventData) {
        if (isPrerender()) {
            return;
        }

        const results = arr.map((item) =>
            Object.assign(item, {
                ...envMixin(),
                ...additional,
                extra: JSON.stringify({ referrer: window.sessionStorage.getItem(`material_${item.item_id}`) })
            })
        ) as QualityEventData[];

        arr.forEach((item) => {
            window.sessionStorage.removeItem(`material_${item.item_id}`);
        });

        return new Promise((resolve, reject) => {
            this.send(results, resolve, reject);
        });
    },

    trackPostponed(data: QualityEventData) {
        const postponed = window.storage.getItem(storageKeys.general.postponedQE)
            ? JSON.parse(window.storage.getItem(storageKeys.general.postponedQE))
            : '';

        if (postponed) {
            this.track({
                ...data,
                ...postponed
            });

            window.storage.removeItem(storageKeys.general.postponedQE);
        }
    }
};

export default qualityEventsHelpers;
